import classNames from 'classnames';
import FontAwesome from 'utils/FA';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import Pagination from 'react-ultimate-pagination-bootstrap-4';
import { compose } from 'redux';
import withPagination from 'hocs/withPagination';
import { withRouter } from 'react-router';
import { titleize, formattedDate, formatCurrency } from '../../utils';
import Placeholder from '../Placeholder';
import { default as duckify, filterDuck, singleDuck } from '../../utils/duckify';
import { billingAccounts, autopayInstallments } from '../../ducks';
import './styles/AutopayInstallmentList.scss';
import './styles/BillingItemList.scss';
import LinkButton from '../LinkButton/LinkButton';
import withFiltering from '../../hocs/withFiltering';

const InstallmentDetails = ({
  itemsByContact, ...props
}) => (
  <React.Fragment>
    <tr {...props}>
      <td className='ap-installment-toggle' colSpan={3} />
      <td className='ap-installment-contact-highlight' colSpan={2}>{itemsByContact.contact}</td>
      <td className='ap-item-total ap-installment-contact-highlight'>{formatCurrency(itemsByContact.total_amount)}</td>
      <td className='ap-item-total-balance ap-installment-contact-highlight'>{formatCurrency(itemsByContact.total_balance)}</td>
    </tr>
    {itemsByContact.items.map((item) => (
      <React.Fragment>
        <tr {...props}>
          <td className='ap-installment-toggle' colSpan={2} />
          <td>{formattedDate(item.billing_date)}</td>
          <td className='ap-item-description'>{item.description}</td>
          <td>{titleize(item.type)}</td>
          <td className='ap-item-amount'>{formatCurrency(item.amount)}</td>
          <td className='ap-installment-balance' />
        </tr>
        {item.amounts.map((itemAmount) => (
          <tr {...props}>
            <td className='ap-installment-toggle' colSpan={2} />
            <td />
            <td className='sub ap-item-description'>{itemAmount.billing_category}</td>
            <td />
            <td className='sub ap-item-amount'>{formatCurrency(itemAmount.amount)}</td>
            <td className='ap-installment-balance' />
          </tr>
        ))}
      </React.Fragment>
    ))}
  </React.Fragment>
);

const Installment = ({
  installment, account, isCurrentDue, followsCurrentDue, ...props
}) => {
  const [collapsed, setCollapsed] = useState(true);

  const displayDetails = () => {
    return installment.items_by_contact.map(itemsByContact => (
      <InstallmentDetails
        itemsByContact={itemsByContact}
        key={itemsByContact.id}
      />
    ));
  };

  const isOverdue = isCurrentDue && installment.total_balance > 0;
  const isPrepaid = isCurrentDue && installment.total_balance < 0;
  const isFirstInstallment = installment.payment_due_date === account.upcoming_autopay_date;

  return (
    <tbody {...props}>
      <tr className={classNames({'ap-installment-overdue': isOverdue}, {'ap-installment-prepaid': isPrepaid}, {'first-ap-installment': isFirstInstallment}, {'first-post-current': followsCurrentDue})}>
        <td className='ap-installment-toggle'>
          <LinkButton
            className='ap-installment-toggle-button'
            content={(
              <React.Fragment>
                <FontAwesome name={collapsed ? 'caret-right' : 'caret-down'} postNbsp />Details
              </React.Fragment>
            )}
            onClick={() => setCollapsed(!collapsed)}
          />
        </td>
        <td className='ap-item-due-date'>{formattedDate(installment.payment_due_date)}</td>
        <td />
        <td className='ap-item-description'>
          {isCurrentDue ? 'Current Balance' : 'Installment'}
        </td>
        <td />
        <td className={classNames('ap-item-amount', { 'negative-number': installment.amount < 0 })}>
          {formatCurrency(installment.total_amount)}
        </td>
        <td className={classNames('ap-item-balance', 'ap-installment-balance', { 'negative-number': installment.balance < 0 })}>
          {formatCurrency(installment.total_balance)}
        </td>
      </tr>
      { !collapsed && displayDetails() }
    </tbody>
  );
};

const InstallmentTable = (
  {
    installments, account
  }
) => {
  const hasCurrentDue = installments.length > 0 && (installments[0].payment_due_date <= account.effective_date);
  return (
    <React.Fragment>
      {installments.map((installment, i) => (
        <Installment
          key={installments.id}
          index={i}
          installment={installment}
          account={account}
          isCurrentDue={i === 0 && hasCurrentDue}
          followsCurrentDue={i === 1 && hasCurrentDue}
        />
      ))}
    </React.Fragment>
  );
};

const AutopayInstallmentList = (
  {
    installments, account, page, setPage, filters, setFilters
  }
) => {
  useEffect(() => {
    setFilters(filters);
  }, []);
  const totalPages = Math.ceil(installments.total / installments.perPage);
  const dueDateTooltip = "The specified date by which a payment is due. For families enrolled in autopay, it's when the payment is automatically withdrawn.";
  return (
    <div>
      <Placeholder.loading ready={installments.ready}>

        {totalPages > 1 && (
          <div>
            <div className='contact-list-react-pagination'>
              <div className='pagination pagination-right'>
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  hideFirstAndLastPageLinks
                  onChange={setPage}
                />
              </div>
            </div>
          </div>
        )}

        <table className='ap-installment-list table table-borderless'>
          <thead>
            <tr>
              <th className='align-center' colSpan={2}>
                <OverlayTrigger placement='bottom' overlay={<Tooltip>{dueDateTooltip}</Tooltip>}>
                  <FontAwesome name='info-circle' postNbsp />
                </OverlayTrigger>
                Payment Due Date
              </th>
              <th>
                <OverlayTrigger placement='bottom' overlay={<Tooltip>The date the charge was added/incurred</Tooltip>}>
                  <FontAwesome name='info-circle' postNbsp />
                </OverlayTrigger>
                Post Date
              </th>
              <th className='ap-item-description'>Description</th>
              <th>Type</th>
              <th className='ap-item-amount'>Amount</th>
              <th className='ap-item-balance ap-installment-balance'>
                <OverlayTrigger placement='bottom' overlay={<Tooltip>Running remaining balance of all past and future charges</Tooltip>}>
                  <FontAwesome name='info-circle' postNbsp />
                </OverlayTrigger>
                Overall Balance
              </th>
            </tr>
          </thead>
          <InstallmentTable installments={installments} account={account} />
          {installments.length === 0 && (
            <tbody>
            <tr>
              <td colSpan='7'><em>No billing installments found.</em></td>
            </tr>
            </tbody>
          )}

        </table>
      </Placeholder.loading>
    </div>
  );
};
AutopayInstallmentList.propTypes = {
  installments: PropTypes.array,

  account: PropTypes.shape({
    effective_date: PropTypes.string
  }).isRequired,

  /** Current page number from `withPagination` */
  page: PropTypes.number.isRequired,

  /** Callback to set page number from `withPagination` */
  setPage: PropTypes.func,

  /** Current filters from 'withFiltering' */
  filters: PropTypes.shape({
    contact_id: PropTypes.string,
    school_year_id: PropTypes.string
  }),

  /** Callback to set filters from `withFiltering` */
  setFilters: PropTypes.func,
};
AutopayInstallmentList.defaultProps = {
  setPage: () => false,
  filters: {},
  setFilters: () => false
};

const mapDucksToProps = ({
  billingAccountId, page, perPage
}) => ({
  account: singleDuck(billingAccounts, billingAccountId),
  installments: filterDuck(autopayInstallments, {
    billing_account_id: billingAccountId,
    page: page || 1,
    per_page: perPage
  })
});

export default compose(
  withRouter,
  withPagination,
  withFiltering,
  duckify(mapDucksToProps, () => ({}))
)(AutopayInstallmentList);
